<script setup lang="ts">
  import {
    addMonthsToDate,
    addYearsToDate,
    setMonthForDate,
    setYearForDate,
    getStartOfMonth,
    getEndOfMonth,
    formatDate,
  } from '@travelata-libs/utils';
  import { computed, ref, watch, inject } from 'vue';
  import { CalendarSize, MonthName } from './Calendar.types';
  import { useCalendar } from './composables/useCalendar';
  import { Button, Select, SelectSize } from '@/components';

  interface CalendarControlsProps {
    double?: boolean;
    mobile?: boolean;
    size?: `${CalendarSize}`;
    minDate?: Date | null;
    maxDate?: Date | null;
  }

  const props = defineProps<CalendarControlsProps>();

  const {
    displayedDate,
    displayedMonth,
    displayedYear,
    displayedMonthYear,
    rangeDisplayedMonthYear,
    getYearsRange,
    getRange,
  } = useCalendar();

  const focusDatepicker = inject<(() => void) | null>('focusDatepicker', null);

  const selectedMonth = ref<string>(displayedMonth.value);
  const selectedYear = ref<number>(displayedYear.value);

  const yearList = computed(() => getYearsRange(props.minDate, props.maxDate));
  const rangeList = computed(() => getRange(props.minDate, props.maxDate));

  const onChangeMonth = (direction: string) => {
    const amount = direction === 'next' ? 1 : -1;

    displayedDate.value = addMonthsToDate(displayedDate.value, amount).getTime();
  };

  const onChangeYear = (direction: string) => {
    const amount = direction === 'next' ? 1 : -1;

    displayedDate.value = addYearsToDate(displayedDate.value, amount).getTime();
  };

  function onChangeSelect(value: MonthName, type: 'month'): void;
  // eslint-disable-next-line no-redeclare
  function onChangeSelect(value: number, type: 'year'): void;
  // eslint-disable-next-line no-redeclare
  function onChangeSelect(value: MonthName | number, type: 'month' | 'year'): void {
    let newDate =
      type === 'month'
        ? setMonthForDate(displayedDate.value, Object.values(MonthName).indexOf(value as MonthName)).getTime()
        : setYearForDate(displayedDate.value, value as number).getTime();

    if (props.minDate && newDate < props.minDate.getTime()) {
      newDate = props.minDate.getTime();
    }

    if (props.maxDate && newDate > props.maxDate.getTime()) {
      newDate = props.maxDate.getTime();
    }

    displayedDate.value = newDate;
  }

  const prevYearIsDisabled = computed(() => {
    if (props.minDate) {
      return addYearsToDate(displayedDate.value, -1).getTime() < props.minDate.getTime();
    }

    return Number(formatDate(addYearsToDate(displayedDate.value, -1), 'yyyy')) < yearList.value[0];
  });

  const nextYearIsDisabled = computed(() => {
    if (props.maxDate) {
      return props.double
        ? addYearsToDate(addMonthsToDate(displayedDate.value, 1), 1).getTime() > props.maxDate.getTime()
        : addYearsToDate(displayedDate.value, 1).getTime() > props.maxDate.getTime();
    }

    return props.double
      ? Number(formatDate(addYearsToDate(addMonthsToDate(displayedDate.value, 1), 1), 'yyyy')) > yearList.value.at(-1)
      : Number(formatDate(addYearsToDate(displayedDate.value, 1), 'yyyy')) > yearList.value.at(-1);
  });

  const prevMonthIsDisabled = computed(() => {
    if (props.minDate) {
      return getStartOfMonth(displayedDate.value) === getStartOfMonth(props.minDate);
    }

    return displayedMonthYear.value === rangeList.value[0];
  });

  const nextMonthIsDisabled = computed(() => {
    if (props.maxDate) {
      return props.double
        ? getEndOfMonth(addMonthsToDate(displayedDate.value, 1)) === getEndOfMonth(props.maxDate)
        : getEndOfMonth(displayedDate.value) === getEndOfMonth(props.maxDate);
    }

    return props.double
      ? displayedMonthYear.value === rangeList.value.at(-2)
      : displayedMonthYear.value === rangeList.value.at(-1);
  });

  const selectDoubleSize = computed(() => (props.size === CalendarSize.M ? SelectSize.M : SelectSize.XL));

  const onBlurSelect = () => {
    focusDatepicker?.();
  };

  watch(
    () => [displayedMonth.value, displayedYear.value],
    ([month, year]) => {
      selectedMonth.value = month as string;
      selectedYear.value = year as number;
    },
  );
</script>

<template>
  <div :class="['tl-calendar-controls', { 'tl-calendar-controls_double': double && !mobile }]">
    <div class="tl-calendar-controls__buttons">
      <Button
        v-if="!double"
        appearance="secondary"
        type="icon"
        icon="double-chevron-left"
        :size="size"
        :disabled="prevYearIsDisabled"
        @click="onChangeYear('prev')"
      />
      <Button
        appearance="secondary"
        type="icon"
        icon="chevron-left"
        :size="size"
        :disabled="prevMonthIsDisabled"
        @click="onChangeMonth('prev')"
      />
    </div>

    <div class="tl-calendar-controls__selects">
      <template v-if="double">
        <Select v-model="displayedMonthYear" :options="rangeList" readonly :size="selectDoubleSize" centered />
        <Select v-model="rangeDisplayedMonthYear" :options="rangeList" readonly :size="selectDoubleSize" centered />
      </template>

      <template v-else>
        <Select
          v-model="selectedMonth"
          :options="Object.values(MonthName)"
          @blur="onBlurSelect"
          @update:modelValue="onChangeSelect($event, 'month')"
        />
        <Select
          v-model="selectedYear"
          :options="yearList"
          @blur="onBlurSelect"
          @update:modelValue="onChangeSelect($event, 'year')"
        />
      </template>
    </div>

    <div class="tl-calendar-controls__buttons">
      <Button
        appearance="secondary"
        type="icon"
        icon="chevron-right"
        :size="size"
        :disabled="nextMonthIsDisabled"
        @click="onChangeMonth('next')"
      />
      <Button
        v-if="!double"
        appearance="secondary"
        type="icon"
        icon="double-chevron-right"
        :size="size"
        :disabled="nextYearIsDisabled"
        @click="onChangeYear('next')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tl-calendar-controls {
    $root: &;

    align-items: center;
    color: var(--color-text-primary);
    display: flex;
    gap: var(--spacing-xs);
    width: 100%;

    &_double {
      #{$root}__selects {
        gap: var(--spacing-xxxl);
      }
    }

    &__selects {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
      width: 100%;

      select {
        width: 100%;
      }
    }

    &__buttons {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
    }
  }
</style>
